/**
 * TimelineContent Component
 *
 * Main SVG canvas with all rendering layers.
 * Layers are rendered bottom-to-top for correct z-ordering.
 */

import React, { useMemo, useEffect, useRef, useCallback } from 'react'
import { Box } from '@mui/material'
import {
  useTimelineContextDispatch,
  setHoverData as setHoverDataAction,
  setActivePhaseKey as setActivePhaseKeyAction,
} from '../context/timeline-context'
import { useTimelineStaticContext } from '../context/timeline-static-context'
import { useTimelineInteractionContext } from '../context/timeline-interaction-context'
import { SELECTOR_BOTTOM_PADDING, WEEK_MS } from '../timeline-constants'
import { usePhaseDrag } from '../../hooks/use-phase-drag'
import { useTaskDrag } from '../../hooks/use-task-drag'
import { useSchedulePan } from '../../hooks/use-schedule-pan'
import { useTaskPan } from '../../hooks/use-task-pan'
import { useSuccessionPan } from '../../hooks/use-succession-pan'
import { useSuccessionDrag } from '../../hooks/use-succession-drag'
import { useSuccessionTaskDrag } from '../../hooks/use-succession-task-drag'
import { useTimelineDragPan } from '../hooks/use-timeline-drag-pan'
import { parseIntervalToDays } from '~/common/utils/date-utils'
import { TimelineHoverLine } from './content/timeline-hover-line'
import { TimelineHoverLineRect } from './content/timeline-hover-line-rect'
import { TimelineWeekBackground } from './content/timeline-week-background'
import { TimelineRowLines } from './content/timeline-row-lines'
import { TimelinePhaseLayer } from './content/timeline-phase-layer'
import { TimelineTaskLayer } from './task/timeline-task-layer'
import { TimelineBookmarkLayer } from './content/timeline-bookmark-layer'
import { TimelineTaskBookmarkLayer } from './task/timeline-task-bookmark-layer'
import { TimelineDragHandlesLayer } from './content/timeline-drag-handles-layer'
import { TimelineTaskDragHandlesLayer } from './task/timeline-task-drag-handles-layer'
import { TimelineContentReportLines } from './content/timeline-content-report-lines'
import { TimelineContentNowLine } from './content/timeline-content-now-line'
import { TimelineTaskOverlay } from './task/timeline-task-overlay'
import { TimelineSuccessionsLayer } from './content/timeline-successions-layer'
import { TimelineTaskTrackerLayer } from './task/timeline-task-tracker-layer'
import { TimelineTaskTrackerSuccessionLayer } from './task/timeline-task-tracker-succession-layer'
import { useEditCropContext } from '~/crop/edit-crop/edit-crop-context'
import { generateNewSuccessionPreview } from '~/crop/edit-crop/generate-new-succession-preview'
import { useFeatures } from '~/common/use-features'

/**
 * TimelineContent - Main SVG canvas
 *
 * Rendering layers (bottom to top):
 * 1. Week background (alternating tints)
 * 2. Row lines (horizontal grid)
 * 3. Phase rectangles (collapsed view) or Bookmarks (expanded view)
 * 4. Drag handles (interactive mode)
 * 5. Frost markers (dashed vertical lines)
 * 6. NOW line (solid vertical line)
 * 7. Hover line (follows mouse)
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data with week-based phases
 * @param {boolean} props.interactive - Enable phase drag editing
 * @param {Function} props.onPhaseAdjust - Phase adjustment callback
 * @param {Function} props.onTaskAdjust - Task adjustment callback
 * @param {boolean} props.forceAllExpand - Force all crops expanded
 * @param {boolean} props.hideRowLines - Hide horizontal grid lines
 */
export const TimelineContent = ({
  crops,
  interactive,
  onPhaseAdjust,
  onTaskAdjust,
  forceAllExpand,
  hideRowLines,
  panAnimationX = 0,
  hideBadges = false,
}) => {
  const {
    TIMELINE_WIDTH,
    NOW_X,
    CUTOFF_X,
    contentRef,
    handleContentScroll,
    updateTracker,
    totalRowsHeight,
    viewStrategy,
    isDraggingHandleRef,
    timelineDateRange,
    frostDateRange,
    hideNowLine,
    printMode,
  } = useTimelineStaticContext()

  const { hoverData } = useTimelineInteractionContext()

  const dispatch = useTimelineContextDispatch()

  // Per-row interactive: use the editing crop (from EditCropContext) if available,
  // otherwise fall back to crops[0] for modal preview (interactive=true).
  const { features } = useFeatures()
  const editCropCtx = useEditCropContext()
  const editingCropId = editCropCtx?.editingCropId ?? null
  const adjustedPhases = editCropCtx?.adjustedPhases ?? null
  const adjustedTasks = editCropCtx?.adjustedTasks ?? null
  const adjustedSuccessionData = editCropCtx?.adjustedSuccessionData ?? null
  const targetSuccessions = editCropCtx?.successions ?? null
  const isEditing = (interactive || Boolean(editingCropId))

  // Stable primitive keys derived from date objects — prevents displayCrops from recomputing
  // when timelineDateRange/frostDateRange get new object references but identical date values.
  const tlStartMs = timelineDateRange?.start?.getTime() ?? null
  const frostFfdMs = frostDateRange?.ffd?.getTime() ?? null

  // Embed per-crop interactivity and convert adjusted phases/tasks to week-format for live rendering
  const displayCrops = useMemo(() => {
    return crops?.map(c => {
      const isInteractive = interactive || c.id === editingCropId
      if (c.id !== editingCropId) {
        return isInteractive ? { ...c, interactive: true } : c
      }
      const result = { ...c, interactive: true }

      if (adjustedPhases) {
        result.phases = adjustedPhases.map(p => {
          const phaseStartMs = new Date(p.est_started_at).getTime()
          const durationDays = parseIntervalToDays(p.est_duration)
          return {
            ...p,
            start: tlStartMs != null ? (phaseStartMs - tlStartMs) / WEEK_MS : p.start,
            len: durationDays / 7,
          }
        })
      }

      if (adjustedTasks) {
        // Merge by ID; filter out tasks absent from adjustedMap (they were removed),
        // but always keep readOnly and completed tasks regardless.
        const adjustedMap = new Map(adjustedTasks.map(t => [t.id, t]))
        result.tasks = c.tasks?.filter(t =>
          t.readOnly || t.state === 'completed' || adjustedMap.has(t.id)
        ).map(t => {
          const adj = adjustedMap.get(t.id)
          if (!adj) return t
          const durationDays = adj.duration ? parseIntervalToDays(adj.duration) : null
          return {
            ...adj,
            start: tlStartMs != null ? (new Date(adj.task_window_start).getTime() - tlStartMs) / WEEK_MS : adj.start,
            len: durationDays != null ? durationDays / 7 : adj.len,
          }
        })
      }

      // Apply adjusted succession child phases/tasks for live rendering
      if (adjustedSuccessionData && c.succession?.children) {
        result.succession = {
          ...c.succession,
          children: c.succession.children.map(child => {
            const adj = adjustedSuccessionData.get(child.id)
            if (!adj) return child
            const childResult = { ...child }
            if (adj.phases) {
              childResult.phases = adj.phases.map(p => {
                const phaseStartMs = new Date(p.est_started_at).getTime()
                const durationDays = parseIntervalToDays(p.est_duration)
                return {
                  ...p,
                  start: tlStartMs != null ? (phaseStartMs - tlStartMs) / WEEK_MS : p.start,
                  len: durationDays / 7,
                }
              })
            }
            if (adj.tasks) {
              const adjTaskMap = new Map(adj.tasks.map(t => [t.id, t]))
              childResult.tasks = child.tasks?.map(t => {
                const adjTask = adjTaskMap.get(t.id)
                if (!adjTask) return t
                const durationDays = adjTask.duration ? parseIntervalToDays(adjTask.duration) : null
                return {
                  ...adjTask,
                  start: tlStartMs != null ? (new Date(adjTask.task_window_start).getTime() - tlStartMs) / WEEK_MS : adjTask.start,
                  len: durationDays != null ? durationDays / 7 : adjTask.len,
                }
              })
            }
            return childResult
          }),
        }
      }

      // Adjust live preview when user changes succession count in the edit popper
      if (targetSuccessions != null) {
        const existingChildren = result.succession?.children ?? []
        if (targetSuccessions > existingChildren.length) {
          // Adding successions — inject preview children
          const lastChild = existingChildren[existingChildren.length - 1]
          const baseIntervalDays = lastChild && existingChildren.length > 0
            ? Math.round((lastChild.interval_days ?? existingChildren.length * 14) / existingChildren.length)
            : 14
          const editingGrowthType = editCropCtx?.editingCropDetails?.growth_code ?? null
          const firstFrostWeek = (frostFfdMs != null && tlStartMs != null)
            ? (frostFfdMs - tlStartMs) / WEEK_MS
            : null
          const previewChildren = generateNewSuccessionPreview({
            existingChildren,
            newCount: targetSuccessions,
            baseIntervalDays,
            growthType: editingGrowthType,
            firstFrostWeek,
            baseCrop: existingChildren.length === 0 ? result : null,
          })
          result.succession = {
            ...(result.succession ?? { count: 0, children: [] }),
            count: targetSuccessions,
            children: [...existingChildren, ...previewChildren],
          }
        } else if (targetSuccessions < existingChildren.length) {
          // Removing successions — slice the last N children off live
          result.succession = {
            ...(result.succession ?? { count: 0, children: [] }),
            count: targetSuccessions,
            children: existingChildren.slice(0, targetSuccessions),
          }
        }
      }

      return result
    })
  }, [crops, editingCropId, adjustedPhases, adjustedTasks, adjustedSuccessionData, targetSuccessions, interactive, tlStartMs, frostFfdMs, editCropCtx?.editingCropDetails?.growth_code])

  const activeCrop = useMemo(() => {
    if (editingCropId) return displayCrops?.find(c => c.id === editingCropId) ?? null
    if (interactive) return displayCrops?.[0] ?? null
    return null
  }, [editingCropId, interactive, displayCrops])

  // Phase drag — scoped to activeCrop only
  const phases = useMemo(() => activeCrop?.phases ?? [], [activeCrop])

  const {
    draggingIndex,
    hoveredIntersection,
    setHoveredIntersection,
    handleDragStart,
  } = usePhaseDrag({ phases, onPhaseAdjust })

  // Task drag — scoped to activeCrop only (exclude read-only context tasks)
  const tasks = useMemo(() => activeCrop?.tasks?.filter(t => !t.readOnly && t.state !== 'completed') ?? [], [activeCrop])

  // Remove a task from the active crop during edit mode
  const handleRemoveTask = useCallback((taskId) => {
    const base = adjustedTasks ?? activeCrop?.tasks ?? []
    onTaskAdjust(base.filter(t => t.id !== taskId))
  }, [adjustedTasks, activeCrop, onTaskAdjust])

  const {
    draggingIndex: taskDraggingIndex,
    hoveredIntersection: taskHoveredIntersection,
    setHoveredIntersection: setTaskHoveredIntersection,
    handleDragStart: handleTaskDragStart,
  } = useTaskDrag({ tasks, onTaskAdjust })

  // Schedule pan — moves all phases and tasks for crops[0] as a rigid body
  const {
    isPanning: isSchedulePanning,
    handlePanStart: handleSchedulePanStart,
  } = useSchedulePan({ phases, onPhaseAdjust, tasks, onTaskAdjust })

  // Task pan — moves a single task for crops[0] independently
  const {
    isPanning: isTaskPanning,
    panningTaskIndex,
    handlePanStart: handleTaskPanStart,
  } = useTaskPan({ tasks, onTaskAdjust })

  // Succession pan — moves all phases+tasks for a succession child as a rigid body
  const {
    isPanning: isSuccessionPanning,
    panningChildId,
    handlePanStart: handleSuccessionPanStart,
  } = useSuccessionPan({
    onSuccessionPhaseAdjust: editCropCtx?.onSuccessionPhaseAdjust ?? null,
    onSuccessionTaskAdjust: editCropCtx?.onSuccessionTaskAdjust ?? null,
  })

  // Succession drag — adjusts phase boundaries on succession child rows
  const {
    isDragging: isSuccessionDragging,
    draggingState: successionDraggingState,
    hoveredState: successionHoveredState,
    setHoveredState: setSuccessionHoveredState,
    handleDragStart: handleSuccessionDragStart,
  } = useSuccessionDrag({
    onSuccessionPhaseAdjust: editCropCtx?.onSuccessionPhaseAdjust ?? null,
  })

  // Succession task drag — adjusts task boundaries on succession child rows
  const {
    isDragging: isSuccessionTaskDragging,
    draggingState: successionTaskDraggingState,
    hoveredState: successionTaskHoveredState,
    setHoveredState: setSuccessionTaskHoveredState,
    handleDragStart: handleSuccessionTaskDragStart,
  } = useSuccessionTaskDrag({
    onSuccessionTaskAdjust: editCropCtx?.onSuccessionTaskAdjust ?? null,
  })

  // Keep isDraggingHandleRef in sync so useTimelineZoom can suppress scroll during handle drags and panning
  useEffect(() => {
    if (isDraggingHandleRef) {
      isDraggingHandleRef.current = draggingIndex !== null || taskDraggingIndex !== null
        || isSchedulePanning || isTaskPanning || isSuccessionPanning || isSuccessionDragging || isSuccessionTaskDragging
    }
  }, [draggingIndex, taskDraggingIndex, isSchedulePanning, isTaskPanning, isSuccessionPanning, isSuccessionDragging, isSuccessionTaskDragging, isDraggingHandleRef])

  // Drag-to-pan for timeline navigation
  const {
    isDragging,
    handleMouseDown,
    cursor,
    atLeftEdge,
    atRightEdge,
  } = useTimelineDragPan({ contentRef })

  const edgeShadow = [
    atLeftEdge && 'inset 10px 0 18px -6px rgba(255, 175, 0, 0.5)',
    atRightEdge && 'inset -10px 0 18px -6px rgba(255, 175, 0, 0.5)',
  ].filter(Boolean).join(', ') || 'none'

  // Pan animation: animate phase/task groups from old position to new (CSS transition on DOM refs)
  const phaseGroupRef = useRef(null)
  const taskGroupRef = useRef(null)
  const handlesGroupRef = useRef(null)

  useEffect(() => {
    if (!panAnimationX) return
    const ANIM_DURATION = 400
    const applyAnimation = (el) => {
      if (!el) return
      el.style.transition = 'none'
      el.style.transform = `translateX(${panAnimationX}px)`
      el.getBoundingClientRect() // Force reflow — same pattern as zoom animation
      el.style.transition = `transform ${ANIM_DURATION}ms ease-out`
      el.style.transform = 'translateX(0px)'
    }
    applyAnimation(phaseGroupRef.current)
    applyAnimation(taskGroupRef.current)
    applyAnimation(handlesGroupRef.current)
    const id = setTimeout(() => {
      if (phaseGroupRef.current) phaseGroupRef.current.style.transform = ''
      if (taskGroupRef.current) taskGroupRef.current.style.transform = ''
      if (handlesGroupRef.current) handlesGroupRef.current.style.transform = ''
    }, ANIM_DURATION + 50)
    return () => clearTimeout(id)
  }, [panAnimationX])

  return (
    <Box
      ref={contentRef}
      onScroll={printMode ? undefined : handleContentScroll}
      onMouseDown={printMode ? undefined : handleMouseDown}
      sx={{
        gridColumn: 1,
        gridRow: 2,
        overflowX: printMode ? 'visible' : 'auto',
        overflowY: printMode ? 'visible' : 'auto',
        position: 'relative',
        cursor: printMode ? 'default' : ((isDragging || isSchedulePanning || isTaskPanning || isSuccessionPanning) ? 'grabbing' : cursor),
        userSelect: (isDragging || isSchedulePanning || isTaskPanning || isSuccessionPanning) ? 'none' : 'auto',
        boxShadow: printMode ? 'none' : edgeShadow,
        transition: 'box-shadow 0.25s ease',
        // Hide scrollbars for all browsers
        scrollbarWidth: 'none', // Firefox
        '&::-webkit-scrollbar': { // Chrome, Safari, Edge
          display: 'none',
        },
      }}
    >
      {/* Hover label — sticky so it stays at the top edge when content is scrolled */}
      <TimelineHoverLineRect x={hoverData.x} dateStr={hoverData.dateStr} show={hoverData.show} />

      {/* Main SVG */}
      <svg
        width={TIMELINE_WIDTH}
        style={{
          display: 'block',
          minHeight: '100%',
          height: Math.max(totalRowsHeight + 2 * SELECTOR_BOTTOM_PADDING, 1),
          cursor: (isDragging || isSchedulePanning || isTaskPanning || isSuccessionPanning) ? 'grabbing' : 'grab'
        }}
        onMouseMove={updateTracker}
        onMouseLeave={() => dispatch(setHoverDataAction({ hoverData: { ...hoverData, show: false } }))}
        onClick={() => dispatch(setActivePhaseKeyAction({ activePhaseKey: null }))}  // Deselect on background click
      >
        {/* Layer 1: Week background */}
        <TimelineWeekBackground />

        {/* Layer 2: Row lines (optional) */}
        {!hideRowLines && <TimelineRowLines crops={displayCrops} />}

        {/* Layer 3: Phase rectangles */}
        <g ref={phaseGroupRef} style={{
          opacity: (viewStrategy === 'phases' || viewStrategy === 'both') ? 1 : 0,
          pointerEvents: (viewStrategy === 'phases' || viewStrategy === 'both') ? 'auto' : 'none',
          transition: 'opacity 0.2s ease',
        }}>
          <TimelinePhaseLayer
            crops={displayCrops}
            interactive={interactive}
            onPanStart={handleSchedulePanStart}
            isSchedulePanning={isSchedulePanning}
          />
          <TimelineBookmarkLayer
            crops={displayCrops}
            forceAllExpand={forceAllExpand}
            interactive={interactive}
            onPanStart={handleSchedulePanStart}
          />
        </g>

        {/* Layer 4: Task rectangles */}
        <g ref={taskGroupRef} style={{
          opacity: (viewStrategy === 'tasks' || viewStrategy === 'both') ? 1 : 0,
          pointerEvents: (viewStrategy === 'tasks' || viewStrategy === 'both') ? 'auto' : 'none',
          transition: 'opacity 0.2s ease',
        }}>
          <TimelineTaskLayer
            crops={displayCrops}
            interactive={interactive}
            onPanStart={handleTaskPanStart}
            panningTaskIndex={panningTaskIndex}
            isTaskPanning={isTaskPanning}
            isSchedulePanning={isSchedulePanning}
            onRemoveTask={isEditing ? handleRemoveTask : undefined}
            hideBadges={hideBadges}
          />
          <TimelineTaskBookmarkLayer
            crops={displayCrops}
            forceAllExpand={forceAllExpand}
            interactive={interactive}
            onPanStart={handleTaskPanStart}
            onRemoveTask={isEditing ? handleRemoveTask : undefined}
          />
          {!isEditing && <TimelineTaskTrackerLayer crops={displayCrops} hideBadges={hideBadges} />}
        </g>

        {/* Layer 5: Succession sub-rows */}
        <TimelineSuccessionsLayer
          crops={displayCrops}
          onPanStart={handleSuccessionPanStart}
          panningChildId={panningChildId}
          isSuccessionPanning={isSuccessionPanning}
          onDragStart={handleSuccessionDragStart}
          draggingState={successionDraggingState}
          hoveredState={successionHoveredState}
          setHoveredState={setSuccessionHoveredState}
          onTaskDragStart={handleSuccessionTaskDragStart}
          taskDraggingState={successionTaskDraggingState}
          taskHoveredState={successionTaskHoveredState}
          setTaskHoveredState={setSuccessionTaskHoveredState}
        />

        {/* Succession tracker: must render after TimelineSuccessionsLayer so capture rects are on top */}
        {!isEditing && <TimelineTaskTrackerSuccessionLayer crops={displayCrops} hideBadges={hideBadges} />}

        {/* handleCrops: only the active crop gets drag handles */}
        {isEditing && (
          <g ref={handlesGroupRef}>
            {/* Layer 6: Phase drag handles */}
            <TimelineDragHandlesLayer
              crops={activeCrop ? [activeCrop] : []}
              forceAllExpand={forceAllExpand}
              draggingIndex={draggingIndex}
              hoveredIntersection={hoveredIntersection}
              setHoveredIntersection={setHoveredIntersection}
              handleDragStart={handleDragStart}
            />

            {/* Layer 7: Task drag handles */}
            <TimelineTaskDragHandlesLayer
              crops={activeCrop ? [activeCrop] : []}
              forceAllExpand={forceAllExpand}
              draggingIndex={taskDraggingIndex}
              hoveredIntersection={taskHoveredIntersection}
              setHoveredIntersection={setTaskHoveredIntersection}
              handleDragStart={handleTaskDragStart}
            />
          </g>
        )}

        {/* Layer 8: Report lines */}
        <TimelineContentReportLines />

        {/* Layer 9: NOW line (fixed position) */}
        {!hideNowLine && <TimelineContentNowLine x={NOW_X} height={totalRowsHeight} />}

        {/* Layer 10: Hover line (follows mouse) */}
        <TimelineHoverLine x={hoverData.x} show={hoverData.show} />

        {/* Layer 11: Task overlay — mask + cutoff line + arrow (feature-flagged) */}
        {features.timeline_task_overlay && (
          <TimelineTaskOverlay visible={CUTOFF_X !== null} x={CUTOFF_X} width={TIMELINE_WIDTH} />
        )}
      </svg>
    </Box>
  )
}
